import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';

const FeaturesSection = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const features = [
    {
      id: 1,
      icon: 'Target',
      title: language === 'bn' ? 'ন্যায্য মূল্য মূল্যায়ন' : 'Fair Price Assessment',
      description: language === 'bn' ? 'এআই তুলনামূলক বিক্রয়, বাজারের প্রবণতা এবং সম্পত্তির বৈশিষ্ট্য বিশ্লেষণ করে নির্ধারণ করে লিস্টিং মূল্য ন্যায্য কিনা।' : "AI analyzes comparable sales, market trends, and property features to determine if the listing price is fair, aggressive, or risky. Covers Sydney suburbs like Bondi, Surry Hills & Parramatta, Melbourne's Carlton, South Yarra & Brunswick, and Brisbane's New Farm, Fortitude Valley & Paddington.",
      color: 'primary',
      action: () => navigate('/fair-price-analysis'),
      actionLabel: language === 'bn' ? 'মূল্য যাচাই করুন →' : 'Check a Price →'
    },
    {
      id: 2,
      icon: 'AlertTriangle',
      title: language === 'bn' ? 'ঝুঁকি চিহ্নিতকরণ' : 'Risk Identification',
      description: language === 'bn' ? 'স্বয়ংক্রিয়ভাবে অতিরিক্ত মূল্য, সম্পত্তির ত্রুটি, অবস্থানগত উদ্বেগ এবং বাজারের সময়ের ঝুঁকি সনাক্ত করে।' : 'Automatically detects potential issues like overpricing, property defects, location concerns, and market timing risks. For UAE properties, cross-references RERA and DLD compliance records.',
      color: 'error',
      action: () => navigate('/risk-assessment'),
      actionLabel: language === 'bn' ? 'ঝুঁকি চিহ্নিত করুন →' : 'Identify Risks →'
    },
    {
      id: 3,
      icon: 'TrendingUp',
      title: language === 'bn' ? 'অফার পরিসীমা গাইডেন্স' : 'Offer Range Guidance',
      description: language === 'bn' ? 'বাজারের ডেটা এবং সম্পত্তি বিশ্লেষণের উপর ভিত্তি করে নির্দিষ্ট সর্বনিম্ন এবং সর্বোচ্চ অফার সুপারিশ পান।' : 'Get specific minimum and maximum offer recommendations based on market data and property analysis. Benchmarked against recent sales in Surry Hills, South Yarra, New Farm, and DLD-registered transactions in Dubai.',
      color: 'success',
      action: () => navigate('/offer-range-guidance'),
      actionLabel: language === 'bn' ? 'অফার পরিসীমা পান →' : 'Get Offer Range →'
    },
    {
      id: 4,
      icon: 'MessageSquare',
      title: language === 'bn' ? 'এজেন্টের প্রশ্ন' : 'Agent Questions',
      description: language === 'bn' ? 'বিক্রয় এজেন্টকে জিজ্ঞাসা করার জন্য লক্ষ্যভিত্তিক প্রশ্নের একটি তালিকা পান।' : 'Receive a list of targeted questions to ask the selling agent to uncover hidden issues and negotiate effectively. Includes suburb-specific questions and RERA/DLD compliance queries for UAE off-plan purchases.',
      color: 'accent',
      action: () => navigate('/agent-questions'),
      actionLabel: language === 'bn' ? 'প্রশ্ন তৈরি করুন →' : 'Generate Questions →'
    },
    {
      id: 5,
      icon: 'FileText',
      title: language === 'bn' ? 'বিস্তারিত প্রতিবেদন' : 'Comprehensive Reports',
      description: language === 'bn' ? 'শক্তি, দুর্বলতা, মূল্য নির্ধারণ এবং প্রতিটি সম্পত্তির জন্য কার্যকর পরবর্তী পদক্ষেপ সহ বিস্তারিত বিশ্লেষণ প্রতিবেদন।' : 'Detailed analysis reports covering strengths, weaknesses, pricing, and actionable next steps for every property. Reports include local suburb context and UAE regulatory status via RERA & DLD.',
      color: 'primary',
      action: () => navigate('/reports'),
      actionLabel: 'View Sample Report →'
    },
    {
      id: 6,
      icon: 'History',
      title: 'Analysis History',
      description: 'Access all your past property analyses anytime. Compare multiple properties across Sydney, Melbourne, and Brisbane suburbs, or track your UAE portfolio against DLD-registered valuations.',
      color: 'secondary',
      action: () => navigate('/history'),
      actionLabel: 'View History →'
    }
  ];

  return (
    <section className="py-12 md:py-20 lg:py-28 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-muted rounded-full text-muted-foreground text-sm md:text-base font-medium mb-4">
            <Icon name="Sparkles" size={16} color="currentColor" />
            <span>Powerful Features</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-foreground mb-4 md:mb-6">
            Everything You Need to Buy Smart
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto">
            ListingLens combines AI technology with Australian property market expertise and UAE regulatory intelligence to give you professional-grade analysis
          </p>
        </div>

        {/* Regional Credibility Badges */}
        <div className="flex flex-wrap justify-center gap-3 mb-10 md:mb-14">
          {[
            { label: 'Sydney: Bondi · Surry Hills · Parramatta', icon: 'MapPin', color: '#00D4FF' },
            { label: 'Melbourne: Carlton · South Yarra · Brunswick', icon: 'MapPin', color: '#00D4FF' },
            { label: 'Brisbane: New Farm · Fortitude Valley · Paddington', icon: 'MapPin', color: '#00D4FF' },
            { label: 'UAE: RERA Registered', icon: 'Shield', color: '#00FF88' },
            { label: 'UAE: DLD Verified', icon: 'BadgeCheck', color: '#00FF88' },
          ]?.map((badge, i) => (
            <div
              key={i}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium border"
              style={{
                background: i < 3 ? 'rgba(0, 212, 255, 0.08)' : 'rgba(0, 255, 136, 0.08)',
                borderColor: i < 3 ? 'rgba(0, 212, 255, 0.3)' : 'rgba(0, 255, 136, 0.3)',
                color: badge?.color
              }}
            >
              <Icon name={badge?.icon} size={13} color={badge?.color} />
              {badge?.label}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {features?.map((feature) => (
            <div
              key={feature?.id}
              onClick={feature?.action}
              className="bg-card rounded-xl border border-border p-6 md:p-8 shadow-elevation-sm transition-all duration-300 group cursor-pointer flex flex-col"
              style={{ transition: 'all 0.3s ease' }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = `var(--color-${feature?.color})`;
                e.currentTarget.style.boxShadow = `0 0 25px rgba(0, 212, 255, 0.15)`;
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = '';
                e.currentTarget.style.boxShadow = '';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div className={`w-14 h-14 md:w-16 md:h-16 rounded-xl bg-${feature?.color}/10 flex items-center justify-center mb-4 md:mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <Icon
                  name={feature?.icon}
                  size={window.innerWidth >= 768 ? 28 : 24}
                  color={`var(--color-${feature?.color})`}
                />
              </div>

              <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground mb-3 md:mb-4">
                {feature?.title}
              </h3>

              <p className="text-sm md:text-base text-muted-foreground leading-relaxed flex-1">
                {feature?.description}
              </p>

              <div
                className="mt-4 text-sm font-semibold flex items-center gap-1"
                style={{ color: `var(--color-${feature?.color})` }}
              >
                {feature?.actionLabel}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 md:mt-16 lg:mt-20 bg-gradient-to-br from-primary/5 to-accent/5 rounded-2xl border border-border p-6 md:p-8 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-sm md:text-base font-medium mb-4 md:mb-6">
                <Icon name="Globe" size={16} color="var(--color-primary)" />
                <span>Australian & UAE Market Focused</span>
              </div>
              <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground mb-4 md:mb-6">
                Built for Australian & UAE Property Markets
              </h3>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-6 md:mb-8">
                Unlike generic property tools, ListingLens is trained on Australian property data across suburbs like Bondi, South Yarra, and New Farm, and integrates UAE regulatory intelligence from RERA and DLD for Dubai and Abu Dhabi buyers.
              </p>
              <div className="space-y-3 md:space-y-4">
                {[
                  'Suburb-level data: Bondi, Surry Hills, Parramatta (Sydney)',
                  'Suburb-level data: Carlton, South Yarra, Brunswick (Melbourne)',
                  'Suburb-level data: New Farm, Fortitude Valley, Paddington (Brisbane)',
                  'UAE: RERA (Real Estate Regulatory Agency) compliance checks',
                  'UAE: DLD (Dubai Land Department) transaction verification',
                  'Integrates with Domain and realestate.com.au listing formats'
                ]?.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon name="Check" size={14} color="var(--color-success)" />
                    </div>
                    <p className="text-sm md:text-base text-foreground">{item}</p>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="mt-8 px-8 py-3 rounded-xl font-semibold text-base transition-all duration-200 cursor-pointer"
                style={{
                  background: 'linear-gradient(135deg, #00D4FF, #0099CC)',
                  color: '#000000',
                  boxShadow: '0 0 20px rgba(0, 212, 255, 0.4)'
                }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 0 30px rgba(0, 212, 255, 0.7)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 212, 255, 0.4)'}
              >
                Start Analysing Properties
              </button>
            </div>

            <div className="relative">
              <div className="bg-card rounded-xl border border-border p-6 md:p-8 shadow-elevation-lg">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name="MapPin" size={24} color="var(--color-primary)" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">Coverage Areas</h4>
                    <p className="text-sm text-muted-foreground">All major Australian cities + UAE</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {[
                    { city: 'Sydney, NSW', info: 'Bondi · Surry Hills · Parramatta · Median $1.18M · +6.2% YoY' },
                    { city: 'Melbourne, VIC', info: 'Carlton · South Yarra · Brunswick · Median $920K · +3.8% YoY' },
                    { city: 'Brisbane, QLD', info: 'New Farm · Fortitude Valley · Paddington · Median $870K · +11.4% YoY' },
                    { city: 'Perth, WA', info: 'Median $780K · +19.6% YoY' },
                    { city: 'Adelaide, SA', info: 'Median $740K · +13.1% YoY' },
                    { city: 'Canberra, ACT', info: 'Median $850K · +4.5% YoY' },
                    { city: 'Dubai, UAE', info: 'RERA Registered · DLD Verified · AED 1.2M avg' },
                    { city: 'Abu Dhabi, UAE', info: 'RERA Compliant · DLD Tracked · AED 980K avg' }
                  ]?.map((item, index) => (
                    <div key={index} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                      <div>
                        <span className="text-sm md:text-base text-foreground">{item?.city}</span>
                        <p className="text-xs text-muted-foreground">{item?.info}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-success" />
                        <button
                          type="button"
                          onClick={() => navigate(`/search-results?location=${encodeURIComponent(item?.city?.split(',')?.[0]?.trim())}`)}
                          className="text-xs md:text-sm font-medium cursor-pointer transition-all duration-200 px-2 py-0.5 rounded-md"
                          style={{
                            color: '#00FF88',
                            background: 'rgba(0, 255, 136, 0.1)',
                            border: '1px solid rgba(0, 255, 136, 0.3)'
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.background = 'rgba(0, 255, 136, 0.2)';
                            e.currentTarget.style.boxShadow = '0 0 8px rgba(0, 255, 136, 0.3)';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = 'rgba(0, 255, 136, 0.1)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          Active
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="absolute -bottom-4 -right-4 bg-accent text-accent-foreground px-6 py-3 rounded-xl shadow-elevation-lg hidden lg:block">
                <div className="flex items-center gap-2">
                  <Icon name="TrendingUp" size={20} color="currentColor" />
                  <span className="font-semibold">100% Coverage</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;